import { FEATURE_FLAGS } from '@/config/features.config';

export type MigrationUser = {
  trialOnboardingRequired?: boolean;
  type?: string;
  atlasHome?: unknown;
  shell?: unknown;
};
export type MigrationQuery = Record<string, string | string[] | undefined>;
export type MigrationReader = (path: string) => Promise<any>;

/** A navigation path is data, never an authority or an enrollment flag. */
export function safeLoginPath(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length > 2048) return undefined;
  let decoded = value;
  for (let i = 0; i < 6; i += 1) {
    if (
      !decoded.startsWith('/') ||
      decoded.startsWith('//') ||
      /[\\@\u0000-\u0020\u007f]/.test(decoded) ||
      decoded.includes('://')
    )
      return undefined;
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) return value;
      decoded = next;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** Fully percent-decodes a path that already passed `safeLoginPath` (same 6-pass bound). */
function decodedLoginPath(path: string): string {
  let decoded = path;
  for (let i = 0; i < 6; i += 1) {
    const next = decodeURIComponent(decoded);
    if (next === decoded) break;
    decoded = next;
  }
  return decoded;
}

/**
 * `/dashboard` with no fragment and only empty query values (`/dashboard?search=`, which the bot list
 * writes into the URL on load) is the ordinary landing, not a destination someone chose. A login that
 * would return there follows the landing decision instead (Atlas `/home` for an admitted account).
 * Anything with a value (`?view=classic`, `?search=acme`, Zendesk params) or a sub-path stays explicit.
 */
export function isBareDashboardPath(path: unknown): boolean {
  const safe = safeLoginPath(path);
  if (!safe) return false;
  let url: URL;
  try {
    url = new URL(decodedLoginPath(safe), 'https://landing.invalid');
  } catch {
    return false;
  }
  if (url.pathname !== '/dashboard' || url.hash) return false;
  return Array.from(url.searchParams.values()).every((value) => value === '');
}

/**
 * Whether the account may be offered Atlas without a live access lookup yet: page flag on, and /auth/me reports
 * both `atlasHome` and `atlasAccess`. Callers still confirm with the live decision (`useAtlasAccess`) before
 * pointing anyone at `/home`; this only decides whether to ask.
 */
export function atlasHomeCandidate(
  user: (MigrationUser & { atlasAccess?: unknown }) | undefined,
  pageFlag = FEATURE_FLAGS.ATLAS_HOME
): boolean {
  return pageFlag === true && user?.atlasHome === true && user?.atlasAccess === true;
}

export function atlasMigrationEligible(
  user: MigrationUser | undefined,
  allowed: boolean,
  pageFlag = FEATURE_FLAGS.ATLAS_HOME
): boolean {
  return pageFlag === true && user?.atlasHome === true && allowed;
}

export function admittedAccess(value: any): boolean {
  const access = value?.access;
  if (access?.allowed !== true) return false;
  if (access.reason === 'staff_bots')
    return (
      Array.isArray(access.botIds) &&
      access.botIds.length > 0 &&
      access.botIds.every((id: unknown) => typeof id === 'string' && /^[0-9a-f]{24}$/.test(id))
    );
  return ['owner', 'admin_enabled'].includes(access.reason) && access.botIds === undefined;
}

export function migrationDestination(input: {
  user?: MigrationUser;
  allowed: boolean;
  pageFlag?: boolean;
  query?: MigrationQuery;
  storedRedirect?: unknown;
  firstAssignedBot?: unknown;
  currentPath?: string;
}): string {
  const { user, query = {} } = input;
  if (user?.trialOnboardingRequired === true && user.type === 'BUSINESS_OWNER')
    return '/onboarding';
  const eligible = atlasMigrationEligible(user, input.allowed, input.pageFlag);
  // Classic is navigation, not a preference (owner decision 2026-10-20). A stored `shell: 'classic'` (the
  // Atlas header's Classic chip wrote it until then) no longer routes anyone: an Atlas-eligible account lands
  // on Atlas at every login and every bare /dashboard. Classic stays one explicit link away
  // (`/dashboard?view=classic`, below). The stored value is kept and reported by /auth/me, but read nowhere.
  const useAtlas = eligible;
  if (query.subscription === 'true') return '/profile#pricing-plans';
  // A bare dashboard target (`/dashboard`, `/dashboard?search=`) is the default landing, not a choice:
  // it falls through to the landing decision so an admitted account still reaches /home (D10).
  const pick = (value: unknown) => (isBareDashboardPath(value) ? undefined : safeLoginPath(value));
  const explicit = pick(query.redirect) || pick(input.storedRedirect);
  // An explicit home URL still cannot bypass the migration/page/access gates.
  if (explicit && !/^\/home(?:[/?#]|$)/.test(decodeURIComponent(explicit))) return explicit;
  if (explicit && eligible) return explicit;
  if (input.currentPath && input.currentPath.split(/[?#]/)[0] !== '/dashboard')
    return input.currentPath;
  if (query.from === 'zendesk' && typeof query.botId === 'string') {
    const params = new URLSearchParams({ from: 'zendesk', botId: query.botId });
    if (query.reinstall === 'true') params.set('reinstall', 'true');
    return `/dashboard?${params}`;
  }
  // An explicit request for the classic dashboard (the Atlas header's Classic chip, the rail's "Experts &
  // knowledge", Classic's own Dashboard links) stays on it, so a new tab or a copied link works too. It
  // applies only to a page being rendered (currentPath); a login lands by the landing decision.
  if (query.view === 'classic' && input.currentPath) return input.currentPath;
  if (useAtlas) return '/home';
  if (input.currentPath) return input.currentPath;
  if (
    user?.type === 'TEAM_MEMBER' &&
    typeof input.firstAssignedBot === 'string' &&
    /^[0-9a-f]{24}$/i.test(input.firstAssignedBot)
  )
    return `/bot/${input.firstAssignedBot}/conversation`;
  return '/dashboard';
}

/**
 * A staff member's assigned bots: `GET /chatbot/assigned` → `{ data: { items: [{ id }] } }`
 * (apps/api/src/chatbot/chatbot.controller.ts `listAssigned`). The API has no `/users/assigned-bots`;
 * the staff landing read that path, got a 404 and silently lost the first-bot route.
 */
export const ASSIGNED_BOTS_PATH = '/chatbot/assigned';

/** Shared by SSR and client login: read the live server decision only for an enrolled account. */
export async function resolveMigrationDestination(
  read: MigrationReader,
  query: MigrationQuery,
  storedRedirect?: unknown,
  knownUser?: MigrationUser,
  currentPath?: string
): Promise<string> {
  const user = knownUser || (await read('/auth/me').catch(() => undefined))?.data?.user;
  const [access, bots] = await Promise.all([
    FEATURE_FLAGS.ATLAS_HOME && user?.atlasHome === true
      ? read('/atlas/access').catch(() => undefined)
      : undefined,
    user?.type === 'TEAM_MEMBER' && !currentPath
      ? read(ASSIGNED_BOTS_PATH).catch(() => undefined)
      : undefined,
  ]);
  return migrationDestination({
    user,
    allowed: admittedAccess(access),
    query,
    storedRedirect,
    firstAssignedBot: bots?.data?.items?.[0]?.id,
    currentPath,
  });
}

export function storeLoginRedirect(
  storage: Pick<Storage, 'setItem' | 'removeItem'>,
  redirect: unknown
): void {
  const safe = safeLoginPath(redirect);
  if (safe) storage.setItem('postLoginRedirect', safe);
  else storage.removeItem('postLoginRedirect');
}

export async function clientLoginDestination(
  token: string,
  query: MigrationQuery
): Promise<string> {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem('postLoginRedirect');
    sessionStorage.removeItem('postLoginRedirect');
  } catch {
    /* Storage is optional. */
  }
  return resolveMigrationDestination(
    async (path) => {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: AbortSignal.timeout(15000),
      });
      if (!response.ok) throw new Error('Navigation lookup unavailable');
      return response.json();
    },
    query,
    stored
  );
}
